import moment from "moment";
import httpMixin from "@/v2/mixins/http.js";
import mapperMixin from "@/v2/mixins/mapper.js";
import shippingZoneMixin from "@/v2/mixins/shipping-zones.js";
import { constructSearchListItems } from "@/v2/models/bo-dropdown-search.js";
import { keyBy } from "@/v2/utils/array";
import * as utils from "@/v2/utils/utilities.js";

export default {
    mixins: [httpMixin, mapperMixin, shippingZoneMixin],
    data() {
        return {
            selectedShippingMethod: null,
            selectedShippingMethodName: null,
            selectedShippingMethodExplicit: false,
            // When true, the watchers below must not pick a method on their own (e.g. an existing
            // document loaded without a shipping line). Cleared by an explicit user selection.
            suppressShippingMethodAutoSelect: false,
            shippingFees: {},
            shippingMethodUses: {},
            shippingZones: this.parseShippingZones(window.bo.shippingZones),
            shippingMethodUrl: window.bo.shippingMethodUrl,
            ignoreCheapestShippingFee: false,
            showUnavailableShippingMethods: false,
            autoSelectCheapestShippingMethod: window.bo.autoSelectCheapestShippingMethod ?? false,
            enableShippingTableRatesFeeCalculation: window.bo.enableShippingTableRatesFeeCalculation,
        };
    },
    watch: {
        shippingMethods(newValue) {
            if (newValue) {
                const methods = keyBy(newValue, "id");
                if (this.selectedShippingMethodExplicit && this.selectedShippingMethodName) {
                    const selectedByName = newValue.find((method) => method.name === this.selectedShippingMethodName);
                    if (selectedByName) {
                        this.selectedShippingMethod = selectedByName;
                        return;
                    }

                    // While suppressed (existing document on load), keep the pin frozen so the
                    // page keeps displaying exactly what was saved; the fallback only happens
                    // once a user interaction lifts the suppression.
                    if (this.suppressShippingMethodAutoSelect) {
                        return;
                    }

                    this.selectedShippingMethodExplicit = false;
                    this.selectedShippingMethodName = null;
                }

                if (this.selectedShippingMethod == null || methods[this.selectedShippingMethod?.id] == null) {
                    if (this.suppressShippingMethodAutoSelect) {
                        return;
                    }
                    this.selectedShippingMethod = newValue[0];
                    this.selectedShippingMethodName = this.selectedShippingMethod?.name ?? null;
                }
            } else {
                this.selectedShippingMethod = null;
                this.selectedShippingMethodName = null;
                this.selectedShippingMethodExplicit = false;
            }
        },
        enrichedShippingMethods(newMethods) {
            if (!this.selectedShippingMethod) return;

            const found = newMethods.find((method) => method.id === this.selectedShippingMethod.id);
            if (found) {
                this.selectedShippingMethod = found;
            }
        },
        selectedShippingMethod(newVal) {
            if (!newVal || newVal.failedLabels !== undefined) return;

            const found = this.enrichedShippingMethods.find((method) => method.id === newVal.id);
            if (found) {
                this.selectedShippingMethod = found;
            }
        },
        shippingFees(newValue) {
            const shippingMethods = Array.isArray(this.shippingMethods) ? this.shippingMethods : [];
            if (!shippingMethods.length) return;

            if (this.selectedShippingMethodExplicit && this.selectedShippingMethodName) {
                const selectedByName = shippingMethods.find(
                    (method) => method.name === this.selectedShippingMethodName
                );
                if (selectedByName) {
                    this.selectedShippingMethod = selectedByName;
                    return;
                }

                // While suppressed, keep the pin frozen (see the shippingMethods watcher).
                if (this.suppressShippingMethodAutoSelect) {
                    return;
                }

                this.selectedShippingMethodExplicit = false;
                this.selectedShippingMethodName = null;
            }

            if (this.suppressShippingMethodAutoSelect) {
                return;
            }

            if (this.ignoreCheapestShippingFee) {
                this.ignoreCheapestShippingFee = false;
                return;
            }

            // If there is a list of available shipping methods, use them first; else fallback to compare use one from all shipping methods
            const eligibleMethods = shippingMethods.filter((shippingMethod) => {
                return this.isShippingMethodAvailable(shippingMethod);
            });
            const candidateMethods = eligibleMethods.length ? eligibleMethods : shippingMethods;

            if (this.autoSelectCheapestShippingMethod) {
                // Cheapest candidate, preferring one whose shipping class matches the cart.
                const prioritisedMethods = this.findShippingMethodsByCartShippingClass(candidateMethods);
                const cheapestMethod =
                    this.findLowestFeeShippingMethod(prioritisedMethods, newValue) ??
                    this.findLowestFeeShippingMethod(candidateMethods, newValue);
                if (cheapestMethod) {
                    this.setSelectedShippingMethod(cheapestMethod, false);
                }
            } else {
                this.setSelectedShippingMethod(candidateMethods[0], false);
            }

            this.runDocumentShippingMethodValidations();
        },
    },
    computed: {
        hasDeliveryAddress() {
            return this.deliveryAddresses.length;
        },
        shippingZone() {
            const postcode = Number(this.deliveryAddress?.postcode ?? this.postcode);
            const area = this.deliveryAddress?.area;
            const state = this.deliveryAddress?.state;
            const country = this.deliveryAddress?.country;

            if (((this.userLoggedIn ?? true) && this.postcode) || (this.hasDeliveryAddress && this.deliveryAddress)) {
                let shippingZone = null;

                top: for (const zone of this.shippingZones) {
                    if (
                        // company's address country if not empty, then must be matched
                        // else can match with any shipping zone
                        (!this.isEmpty(country) && country == zone.country) ||
                        (this.isEmpty(country) && this.isEmpty(zone.country))
                    ) {
                        for (const region of Object.values(zone.zone_regions)) {
                            if (region.type == "postcode") {
                                if (Number(region.postcode_from) >= 0 && Number(region.postcode_to) >= 0) {
                                    if (
                                        postcode >= Number(region.postcode_from) &&
                                        postcode <= Number(region.postcode_to)
                                    ) {
                                        shippingZone = zone;
                                        break top;
                                    }
                                }
                                if (Number(region.postcode_from) >= 0 && postcode == Number(region.postcode_from)) {
                                    shippingZone = zone;
                                    break top;
                                }
                                if (Number(region.postcode_to) >= 0 && postcode == Number(region.postcode_to)) {
                                    shippingZone = zone;
                                    break top;
                                }
                            }

                            if (region.type == "state") {
                                if (region.state && state == region.state) {
                                    shippingZone = zone;
                                    break top;
                                }
                            }

                            if (region.type == "area") {
                                if (region.area && area == region.area) {
                                    shippingZone = zone;
                                    break top;
                                }
                            }
                        }
                    }
                }

                // company's address country is not empty, but not matched shipping zone
                // can match with shipping zone with empty country
                if (shippingZone === null) {
                    top: for (const zone of this.shippingZones) {
                        if (this.isEmpty(zone.country)) {
                            for (const region of Object.values(zone.zone_regions)) {
                                if (region.type == "postcode") {
                                    if (Number(region.postcode_from) >= 0 && Number(region.postcode_to) >= 0) {
                                        if (
                                            postcode >= Number(region.postcode_from) &&
                                            postcode <= Number(region.postcode_to)
                                        ) {
                                            shippingZone = zone;
                                            break top;
                                        }
                                    }
                                    if (Number(region.postcode_from) >= 0 && postcode == Number(region.postcode_from)) {
                                        shippingZone = zone;
                                        break top;
                                    }
                                    if (Number(region.postcode_to) >= 0 && postcode == Number(region.postcode_to)) {
                                        shippingZone = zone;
                                        break top;
                                    }
                                }

                                if (region.type == "state") {
                                    if (region.state && state == region.state) {
                                        shippingZone = zone;
                                        break top;
                                    }
                                }

                                if (region.type == "area") {
                                    if (region.area && area == region.area) {
                                        shippingZone = zone;
                                        break top;
                                    }
                                }
                            }
                        }
                    }
                }

                return shippingZone;
            }

            return null;
        },
        shippingMethod() {
            if (this.shippingZone) {
                return this.selectedShippingMethod;
            }

            return null;
        },
        shippingMethods() {
            if (this.shippingZone) {
                return Object.values(this.shippingZone.shipping_methods)
                    .filter((shippingMethod) => shippingMethod.status !== false)
                    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.id - b.id);
            }
        },
        /**
         * Every method in the zone with its availability state, failed labels and message.
         * Kept separate from shippingMethodItems because that one hides the incompatible methods
         * behind the "show unavailable" toggle — the selected method must still be resolvable
         * against its enriched form even while hidden, or the control has no message to show.
         */
        enrichedShippingMethods() {
            if (!this.shippingZone) {
                return [];
            }

            return this.shippingMethods.map((shippingMethod) => {
                const state = this.getShippingMethodAvailabilityState(shippingMethod);
                const failedLabels = this.getShippingMethodFailedLabels(shippingMethod);
                const unavailableMessage = this.composeShippingMethodAvailabilityMessage(
                    shippingMethod,
                    moment(this.shippingDate ?? this.documentDeliveryDate).format("DD/MM/YYYY")
                );

                return {
                    ...shippingMethod,
                    availabilityTier: state.tier,
                    isAvailable: state.tier === "available",
                    disabled: state.tier === "incompatible",
                    unavailableReasonCode: state.reasonCode,
                    failedLabels,
                    unavailableMessage,
                    hasQuery: (query) => shippingMethod.name.toLowerCase().includes(query),
                };
            });
        },
        shippingMethodItems() {
            return this.buildShippingMethodItems();
        },
        classIncompatibleShippingMethodCount() {
            if (!this.shippingZone || !Array.isArray(this.shippingMethods)) {
                return 0;
            }

            return this.shippingMethods.filter(
                (shippingMethod) => this.getShippingMethodAvailabilityState(shippingMethod).tier === "incompatible"
            ).length;
        },
        hasHiddenUnavailableShippingMethods() {
            return this.classIncompatibleShippingMethodCount > 0;
        },
    },
    methods: {
        ...utils,

        toggleUnavailableShippingMethods() {
            this.showUnavailableShippingMethods = !this.showUnavailableShippingMethods;

            // The dropdown is positioned by Popper, which doesn't reposition on its own when the
            // menu height changes (it leaves a stale transform, detaching the menu). Bootstrap's
            // Popper listens for window resize, so nudge it once the new content has rendered.
            this.$nextTick(() => {
                window.dispatchEvent(new Event("resize"));
            });
        },
        isShippingMethodAvailable(method) {
            return this.getShippingMethodAvailabilityState(method).tier === "available";
        },
        /**
         * Categorises a shipping method into one of three tiers and the reason it failed:
         *   - "available" : passes every check (shown on top)
         *   - "adjustable" : fails order amount / table rate (shown; the customer can fix it by
         *                    changing their order)
         *   - "incompatible": shipping class doesn't fit the cart, or the quota is used up — can't be
         *                     fixed by adjusting the order (hidden by default behind the toggle)
         */
        getShippingMethodAvailabilityState(method) {
            if (!method) {
                return { tier: "adjustable", reasonCode: null };
            }

            // Shipping class — a method with no shipping classes applies to all classes.
            // A hard class mismatch is hidden by default.
            if (!this.isShippingMethodShippingClassEligible(method)) {
                return { tier: "incompatible", reasonCode: "shipping_class" };
            }

            // Order amount
            const subtotal = Number(this.shippingMethodSubtotal ?? 0);
            if (subtotal < Number(method.min_order_amount ?? 0)) {
                return { tier: "adjustable", reasonCode: "min_order" };
            }
            const maxOrderAmount = Number(method.max_order_amount ?? 0);
            if (maxOrderAmount !== 0 && subtotal > maxOrderAmount) {
                return { tier: "adjustable", reasonCode: "max_order" };
            }

            // Weight / volume / dimension (table rate)
            const tableRateFailure = this.getShippingMethodTableRateFailure(method);
            if (tableRateFailure) {
                return { tier: "adjustable", reasonCode: tableRateFailure };
            }

            // Remaining uses (quota) — only enforced when a quota is known for this method.
            // An exhausted quota can't be fixed by adjusting the order, so it's hidden like a
            // shipping-class mismatch.
            const remainingUses = this.shippingMethodUses[method.id]?.remaining_uses;
            if (remainingUses != null && Number(remainingUses) <= 0) {
                return { tier: "incompatible", reasonCode: "quota" };
            }

            return { tier: "available", reasonCode: null };
        },
        /**
         * Returns a map of which individual labels fail their constraint, so the UI can highlight
         * exactly the failing label(s) in red — independent of the tier/first-failure reasonCode,
         * and regardless of whether the method is adjustable or incompatible.
         */
        getShippingMethodFailedLabels(method) {
            const failed = {};
            if (!method) {
                return failed;
            }

            if (!this.isShippingMethodShippingClassEligible(method)) {
                failed.shipping_class = true;
            }

            const subtotal = Number(this.shippingMethodSubtotal ?? 0);
            if (subtotal < Number(method.min_order_amount ?? 0)) {
                failed.min_spend = true;
            }
            const maxOrderAmount = Number(method.max_order_amount ?? 0);
            if (maxOrderAmount !== 0 && subtotal > maxOrderAmount) {
                failed.max_spend = true;
            }

            if (method.type === "table_rate") {
                const totals = this.getTableRateTotalsByUnit();
                const totalByUnit = Number(totals?.[method.table_rate_unit] ?? 0);
                const minTableRate = Number(method.min_table_rate ?? 0);
                const maxTableRate = Number(method.max_table_rate ?? 0);
                // Only flag a bound that is actually configured (> 0), mirroring the min/max
                // weight/volume/dimension labels which render only when the bound > 0.
                if (maxTableRate > 0 && totalByUnit > maxTableRate) {
                    failed.max_table_rate = true;
                }
                if (minTableRate > 0 && totalByUnit < minTableRate) {
                    failed.min_table_rate = true;
                }
            }

            const remainingUses = this.shippingMethodUses[method.id]?.remaining_uses;
            if (remainingUses != null && Number(remainingUses) <= 0) {
                failed.remaining = true;
            }

            return failed;
        },
        getShippingMethodTableRateFailure(method, tableRateTotals = null) {
            if (method?.type !== "table_rate") {
                return null;
            }

            const tableRateUnit = method?.table_rate_unit;
            const totals = tableRateTotals ?? this.getTableRateTotalsByUnit();
            const totalByUnit = Number(totals?.[tableRateUnit] ?? 0);
            const minTableRate = Number(method?.min_table_rate ?? 0);
            const maxTableRate = Number(method?.max_table_rate ?? 0);

            if (maxTableRate > 0 && totalByUnit > maxTableRate) {
                return "table_rate_max";
            }
            if (minTableRate > 0 && totalByUnit < minTableRate) {
                return "table_rate_min";
            }

            return null;
        },
        getShippingMethodUnavailableMessage(reasonCode) {
            switch (reasonCode) {
                case "min_order":
                case "table_rate_min":
                    return this.generalTranslations?.increase_order_to_qualify ?? "";
                case "max_order":
                case "table_rate_max":
                    return this.generalTranslations?.order_exceeds_limit ?? "";
                case "shipping_class":
                case "quota":
                    return this.generalTranslations?.shipping_method_not_available ?? "";
                default:
                    return "";
            }
        },
        buildShippingMethodItems() {
            if (!this.shippingZone) {
                return [];
            }

            const methods = this.enrichedShippingMethods;

            // Tier 1: available on top.
            // Tier 2: order amount / table rate failures (shown; the customer can adjust their order to make these work).
            const available = methods.filter((shippingMethod) => shippingMethod.availabilityTier === "available");
            const adjustable = methods.filter((shippingMethod) => shippingMethod.availabilityTier === "adjustable");
            // Tier 3: shipping-class mismatch or exhausted quota — hidden until the user expands the toggle.
            const incompatible = methods.filter((shippingMethod) => shippingMethod.availabilityTier === "incompatible");

            const shown = [...available, ...adjustable];
            const groups = shown.map((shippingMethod) => {
                return { items: [shippingMethod] };
            });

            if (shown.length > 0) {
                // There are selectable methods on top, so the incompatible ones are tucked behind
                // a toggle. The sentinel renders between the two sections, so the "Show / Hide
                // Unavailable Options" button keeps its position when expanding/collapsing.
                // disabled: true so bo-dropdown-search's select() ignores clicks on it.
                if (incompatible.length > 0) {
                    groups.push({ items: [{ isToggle: true, disabled: true }] });
                    if (this.showUnavailableShippingMethods) {
                        incompatible.forEach((shippingMethod) => {
                            groups.push({ items: [shippingMethod] });
                        });
                    }
                }
            } else {
                // Nothing is selectable — the empty-state banner shows, so there's no toggle to
                // expand; list the incompatible methods directly instead.
                incompatible.forEach((shippingMethod) => {
                    groups.push({ items: [shippingMethod] });
                });
            }
            return constructSearchListItems(groups);
        },

        resolveShippingMethod(methodOrIdOrName) {
            const shippingMethods = Array.isArray(this.shippingMethods) ? this.shippingMethods : [];

            if (!methodOrIdOrName) {
                return null;
            }

            if (typeof methodOrIdOrName === "object") {
                return (
                    shippingMethods.find((method) => method.id === methodOrIdOrName.id) ??
                    shippingMethods.find((method) => method.name === methodOrIdOrName.name) ??
                    null
                );
            }

            return (
                shippingMethods.find((method) => method.id === methodOrIdOrName) ??
                shippingMethods.find((method) => method.name === methodOrIdOrName) ??
                null
            );
        },
        setSelectedShippingMethod(methodOrIdOrName, explicit = false) {
            const resolvedMethod = this.resolveShippingMethod(methodOrIdOrName);

            this.selectedShippingMethod = resolvedMethod;
            this.selectedShippingMethodName = resolvedMethod?.name ?? null;
            this.selectedShippingMethodExplicit = explicit && resolvedMethod != null;

            if (resolvedMethod == null) {
                this.selectedShippingMethodExplicit = false;
            }

            if (this.selectedShippingMethodExplicit) {
                this.suppressShippingMethodAutoSelect = false;
            }
        },
        /**
         * Re-applies the explicit keep-by-name rule against the current method list, falling
         * back to auto-select when the pinned name is gone. Used after a user interaction
         * lifts `suppressShippingMethodAutoSelect`, because the watchers may already have run
         * (and kept the pin frozen) for that same change.
         */
        reapplyShippingMethodSelection() {
            const shippingMethods = Array.isArray(this.shippingMethods) ? this.shippingMethods : [];

            if (this.selectedShippingMethodExplicit && this.selectedShippingMethodName) {
                const selectedByName = shippingMethods.find(
                    (method) => method.name === this.selectedShippingMethodName
                );
                if (selectedByName) {
                    this.selectedShippingMethod = selectedByName;
                    return;
                }

                this.selectedShippingMethodExplicit = false;
                this.selectedShippingMethodName = null;
            }

            this.selectedShippingMethod = shippingMethods[0] ?? null;
            this.selectedShippingMethodName = this.selectedShippingMethod?.name ?? null;
        },

        getCartItemsForShippingClassSelection() {
            if (Array.isArray(this.items)) return this.items;
            if (this.cart?.items) return Object.values(this.cart.items);
            return [];
        },
        getShippingClassName(value) {
            const name = String(value ?? "").trim();
            return name ? name : null;
        },
        getItemShippingClassNames(item) {
            const names = [];

            if (item?._type == "bundle_cart_item" && item?.composite_children) {
                Object.values(item.composite_children).forEach((child) => {
                    const childShippingClass = child?.product?.variations?.[child?.product_id]?.shipping_class?.name;
                    const childShippingClassName = this.getShippingClassName(childShippingClass);
                    if (childShippingClassName) names.push(childShippingClassName);
                });
            } else {
                const selectedVariation =
                    typeof item?.product?.getSelectedVariation === "function"
                        ? item.product.getSelectedVariation()
                        : null;
                const variationById = item?.product?.variations?.[item?.product_id] ?? null;
                const shippingClassName = this.getShippingClassName(
                    selectedVariation?.shipping_class?.name ??
                        variationById?.shipping_class?.name ??
                        item?.product?.shipping_class?.name
                );

                if (shippingClassName) names.push(shippingClassName);
            }

            return [...new Set(names)];
        },
        getCartShippingClassNames() {
            const names = [];

            this.getCartItemsForShippingClassSelection().forEach((item) => {
                names.push(...this.getItemShippingClassNames(item));
            });

            return [...new Set(names)];
        },
        getShippingMethodClassNames(method) {
            const shippingClasses = Object.values(method?.shipping_classes ?? {});

            return shippingClasses
                .filter((shippingClass) => String(shippingClass?.id ?? "").toLowerCase() !== "all")
                .map((shippingClass) => this.getShippingClassName(shippingClass?.name))
                .filter((name) => name != null);
        },
        hasUniversalShippingClass(method) {
            const shippingClasses = Object.values(method?.shipping_classes ?? {});
            return shippingClasses.some((shippingClass) => String(shippingClass?.id ?? "").toLowerCase() === "all");
        },
        isShippingMethodShippingClassEligible(method) {
            if (this.hasUniversalShippingClass(method)) {
                return true;
            }

            const methodClassNames = this.getShippingMethodClassNames(method);
            if (!methodClassNames.length) {
                return true;
            }

            const methodClassSet = new Set(methodClassNames);
            const cartItems = this.getCartItemsForShippingClassSelection();

            return cartItems.every((item) => {
                const itemClassNames = this.getItemShippingClassNames(item);
                if (!itemClassNames.length) {
                    return false;
                }

                return itemClassNames.every((itemClassName) => methodClassSet.has(itemClassName));
            });
        },
        findShippingMethodsByCartShippingClass(methods) {
            const cartShippingClassNames = this.getCartShippingClassNames();
            if (!cartShippingClassNames.length) return null;

            const matchingMethods = [];

            methods.forEach((method) => {
                if (this.hasUniversalShippingClass(method)) {
                    matchingMethods.push(method);
                    return;
                }

                const methodClassNames = this.getShippingMethodClassNames(method);
                if (!methodClassNames.length) return;

                const methodClassSet = new Set(methodClassNames);
                const supportsAllCartClasses = cartShippingClassNames.every((cartClassName) => {
                    return methodClassSet.has(cartClassName);
                });

                if (supportsAllCartClasses) matchingMethods.push(method);
            });

            return matchingMethods;
        },
        getTableRateTotalsByUnit() {
            const totals = {
                kg: 0,
                "m³": 0,
                m: 0,
            };

            const cartItems = this.getCartItemsForShippingClassSelection();
            if (!cartItems.length) return totals;

            cartItems.forEach((item) => {
                if (item?._type == "bundle_cart_item") {
                    Object.values(item?.composite_children ?? {}).forEach((children) => {
                        const selectedVariation = children?.product?.variations?.[children?.product_id] ?? null;
                        const childQuantity = Number(children?.quantity ?? 0);

                        const totalWeight = Number(selectedVariation?.weight ?? 0);
                        if (totalWeight > 0) {
                            totals.kg += totalWeight * childQuantity;
                        }

                        const length = Number(selectedVariation?.length ?? 0);
                        const width = Number(selectedVariation?.width ?? 0);
                        const height = Number(selectedVariation?.height ?? 0);

                        const totalVolume = length * width * height;
                        if (totalVolume > 0) {
                            totals["m³"] += totalVolume * childQuantity;
                        }

                        const totalDimensionalSum = length + width + height;
                        if (totalDimensionalSum > 0) {
                            totals.m += totalDimensionalSum * childQuantity;
                        }
                    });

                    return;
                }

                const selectedVariation =
                    typeof item?.product?.getSelectedVariation === "function"
                        ? item.product.getSelectedVariation()
                        : null;
                const itemQuantity = Number(item?.quantity ?? 0);

                const totalWeight = Number(selectedVariation?.weight ?? 0);
                if (totalWeight > 0) {
                    totals.kg += totalWeight * itemQuantity;
                }

                const length = Number(selectedVariation?.length ?? 0);
                const width = Number(selectedVariation?.width ?? 0);
                const height = Number(selectedVariation?.height ?? 0);

                const totalVolume = length * width * height;
                if (totalVolume > 0) {
                    totals["m³"] += totalVolume * itemQuantity;
                }

                const totalDimensionalSum = length + width + height;
                if (totalDimensionalSum > 0) {
                    totals.m += totalDimensionalSum * itemQuantity;
                }
            });

            return totals;
        },
        isShippingMethodTableRateEligible(method, tableRateTotals = null) {
            if (method?.type !== "table_rate") return true;

            const tableRateUnit = method?.table_rate_unit;
            const totals = tableRateTotals ?? this.getTableRateTotalsByUnit();
            const totalByUnit = Number(totals?.[tableRateUnit] ?? 0);
            const minTableRate = Number(method?.min_table_rate ?? 0);
            const maxTableRate = Number(method?.max_table_rate ?? 0);

            if (maxTableRate > 0 && totalByUnit > maxTableRate) {
                return false;
            }

            return minTableRate > 0 ? totalByUnit >= minTableRate : true;
        },
        runDocumentShippingMethodValidations() {
            if (typeof this.validateDocumentShippingMethodAvailability === "function") {
                this.validateDocumentShippingMethodAvailability();
            }
        },
        /**
         * Builds a single combined message describing every way the selected shipping method is
         * unavailable, in the sequence: shipping class → min/max spend → table rate → quota.
         * If shipping class fails it leads the message and the remaining fragments use their "also"
         * variant; otherwise fragments are joined in their standalone form. Returns "" when the
         * method passes every check. Shared by the cart and admin document pages; the per-page item
         * shape is handled by getShippingMethodFailedLabels via the adapter overrides.
         */
        composeShippingMethodAvailabilityMessage(method, formattedDate) {
            if (!method) {
                return "";
            }

            const t = this.generalTranslations ?? {};
            const failed = this.getShippingMethodFailedLabels(method);
            const classFailed = !!failed.shipping_class;
            const currencySymbol = this.currencySymbol ?? "";
            const unit = method.table_rate_unit;
            const objectLabel =
                unit === "kg" ? t.weight : unit === "m³" ? t.volume : unit === "m" ? t.dimensional_sum : "";

            const parts = [];
            // "also" continuation applies only when the shipping-class fragment leads the message.
            const pick = (key, alsoKey) => (classFailed && parts.length > 0 ? t[alsoKey] ?? t[key] : t[key]);
            const fill = (template, vars) =>
                Object.entries(vars).reduce((text, [token, value]) => text.split(token).join(value), template ?? "");

            if (classFailed) {
                parts.push(t.shipping_method_items_incompatible ?? "");
            }

            if (failed.min_spend) {
                parts.push(
                    fill(pick("shipping_method_min_order_required", "shipping_method_min_order_required_also"), {
                        ":currency_symbol": currencySymbol,
                        ":amount": method.min_order_amount,
                    })
                );
            } else if (failed.max_spend) {
                parts.push(
                    fill(pick("shipping_method_max_order_only", "shipping_method_max_order_only"), {
                        ":currency_symbol": currencySymbol,
                        ":amount": method.max_order_amount,
                    })
                );
            }

            if (failed.min_table_rate) {
                parts.push(
                    fill(pick("shipping_method_table_min_required", "shipping_method_table_min_required_also"), {
                        ":object": objectLabel,
                        ":min_amount": method.min_table_rate,
                        ":unit": unit,
                    })
                );
            } else if (failed.max_table_rate) {
                parts.push(
                    fill(pick("shipping_method_table_max_limit", "shipping_method_table_max_limit_also"), {
                        ":object": objectLabel,
                        ":max_amount": method.max_table_rate,
                        ":unit": unit,
                    })
                );
            }

            if (failed.remaining) {
                parts.push(
                    fill(pick("shipping_method_quota_unavailable", "shipping_method_quota_unavailable_also"), {
                        ":name": method.name,
                        ":date": formattedDate,
                    })
                );
            }

            const capitalize = (text) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
            const message = parts
                .filter((part) => part)
                .map(capitalize)
                .join(". ");

            return message ? `${message}.` : "";
        },
        findLowestFeeShippingMethod(methods, shippingFeesByMethodId) {
            if (!Array.isArray(methods) || !methods.length) return null;

            let lowest = Number.MAX_SAFE_INTEGER;
            let lowestFeeMethod = null;

            methods.forEach((method) => {
                const shippingFeeInfo = shippingFeesByMethodId[method.id];
                if (!shippingFeeInfo) return;

                if (shippingFeeInfo.shipping_fee < lowest) {
                    lowest = shippingFeeInfo.shipping_fee;
                    lowestFeeMethod = method;
                }
            });

            return lowestFeeMethod;
        },

        refreshShippingFees(url, id, success = null, showLoadingScreen = true) {
            if (this.shippingZone) {
                this.calculateShippingFee(
                    url,
                    id,
                    (this.shippingMethods ?? []).map((shippingMethod) => shippingMethod.id),
                    (shippingFees) => {
                        this.shippingFees = keyBy(shippingFees, "shipping_method_id");

                        if (success) {
                            success(shippingFees);
                        }
                    },
                    null,
                    showLoadingScreen
                );
            }
        },
        refreshShippingFeesForNewDocument(url, payload, success = null) {
            if (this.shippingZone && this.enableShippingTableRatesFeeCalculation) {
                payload["_calculate_shipping_fee"] = true;
                this.calculateShippingFeeForNewDocument(
                    url,
                    payload,
                    (this.shippingMethods ?? []).map((shippingMethod) => shippingMethod.id),
                    (shippingFees) => {
                        this.shippingFees = keyBy(shippingFees, "shipping_method_id");

                        if (success) {
                            success(shippingFees);
                        }
                    }
                );
            }
        },
        refreshShippingMethodAvailability(date, showLoadingScreen = true) {
            if (this.shippingZone) {
                this.getShippingMethodsRemainingUses(
                    this.shippingMethodUrl,
                    date,
                    (this.shippingMethods ?? []).map((shippingMethod) => shippingMethod.id),
                    (shippingMethodUses) => {
                        this.shippingMethodUses = keyBy(shippingMethodUses, "shipping_method_id");
                    },
                    null,
                    showLoadingScreen
                );
            }
        },
    },
};
